import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "../lib/cn";
import { Label } from "./label";

/**
 * RHF-free presentational form primitives.
 *
 * The app-side react-hook-form binding composes these by wrapping a field in
 * <FormFieldStateContext.Provider value={{ invalid, errorMessage }}> and
 * rendering <FormItem> inside. All id/aria wiring (label ↔ control ↔
 * description ↔ message) is handled here via context.
 */

export interface FormFieldState {
  invalid: boolean;
  /** Localized error text rendered by <FormMessage> when no children are given. */
  errorMessage?: string;
}

const FormFieldStateContext = React.createContext<FormFieldState>({ invalid: false });

/** Field validity state supplied by the app-side form binding (defaults to valid). */
export function useFormFieldState(): FormFieldState {
  return React.useContext(FormFieldStateContext);
}

export interface FormItemContextValue {
  id: string;
  formItemId: string;
  formDescriptionId: string;
  formMessageId: string;
}

const FormItemContext = React.createContext<FormItemContextValue | null>(null);

/** Ids generated by the enclosing <FormItem>; throws outside one. */
export function useFormItem(): FormItemContextValue {
  const context = React.useContext(FormItemContext);
  if (context === null) {
    throw new Error("useFormItem must be used within <FormItem>");
  }
  return context;
}

const FormItem = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const id = React.useId();
    const value = React.useMemo<FormItemContextValue>(
      () => ({
        id,
        formItemId: `${id}-control`,
        formDescriptionId: `${id}-description`,
        formMessageId: `${id}-message`,
      }),
      [id],
    );
    return (
      <FormItemContext.Provider value={value}>
        <div ref={ref} className={cn("flex flex-col gap-1.5", className)} {...props} />
      </FormItemContext.Provider>
    );
  },
);
FormItem.displayName = "FormItem";

export interface FormLabelProps extends React.ComponentPropsWithoutRef<typeof Label> {
  /** Renders a visual required marker (*) after the label text. */
  required?: boolean;
}

const FormLabel = React.forwardRef<React.ComponentRef<typeof Label>, FormLabelProps>(
  ({ className, required = false, children, ...props }, ref) => {
    const { formItemId } = useFormItem();
    const { invalid } = useFormFieldState();
    return (
      <Label
        ref={ref}
        htmlFor={formItemId}
        className={cn(invalid && "text-destructive", className)}
        {...props}
      >
        {children}
        {required ? (
          <span aria-hidden="true" className="ms-0.5 text-destructive">
            *
          </span>
        ) : null}
      </Label>
    );
  },
);
FormLabel.displayName = "FormLabel";

const FormControl = React.forwardRef<HTMLElement, React.ComponentPropsWithoutRef<typeof Slot>>(
  (props, ref) => {
    const { formItemId, formDescriptionId, formMessageId } = useFormItem();
    const { invalid } = useFormFieldState();
    return (
      <Slot
        ref={ref}
        id={formItemId}
        aria-describedby={invalid ? `${formDescriptionId} ${formMessageId}` : formDescriptionId}
        aria-invalid={invalid || undefined}
        {...props}
      />
    );
  },
);
FormControl.displayName = "FormControl";

const FormDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => {
  const { formDescriptionId } = useFormItem();
  return (
    <p
      ref={ref}
      id={formDescriptionId}
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  );
});
FormDescription.displayName = "FormDescription";

const FormMessage = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, children, ...props }, ref) => {
  const { formMessageId } = useFormItem();
  const { errorMessage } = useFormFieldState();
  const body = children ?? errorMessage;
  if (body === undefined || body === null || body === "") {
    return null;
  }
  return (
    <p
      ref={ref}
      id={formMessageId}
      className={cn("text-sm font-medium text-destructive", className)}
      {...props}
    >
      {body}
    </p>
  );
});
FormMessage.displayName = "FormMessage";

export { FormItem, FormLabel, FormControl, FormDescription, FormMessage, FormFieldStateContext };
